import { useEffect, useState } from "react";
import { TruckInfoWrapper } from "../TruckInfo/TruckInfo.styles";
import DatePickerDateField from "components/DatePicker/components/DatePickerDateField";
import AutoComplete from "components/Common/Autocomplete/Autocomplete";
import { RequestDateWrapper, TruckShippingInfoContent, TruckStopWrapper } from "./TruckShippingInfo.styles";
import { models } from "types/api/viewModels";
import AddIcon from "@mui/icons-material/Add";
import { Button, FormHelperText } from "@mui/material";
import {
	AddNewAddressLink,
	AddStopText,
	RemoveText,
	RequestDateText,
	SelectShippingAddressErrorText,
	SelectShippingAddressText,
	ShippingInfoText,
	StopNumberText
} from "constants/text";
import AddressFormProvider from "pages/Replacements/forms/AddressForm/AddressFormProvider";
import AddNewAddressModal from "pages/Replacements/components/AddNewAddressModal/AddNewAddressModal";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { isDateWithinBusinessDayRange } from "utils/date";
import { END_BUSINESS_DAY_RANGE, START_BUSINESS_DAY_RANGE } from "../stepOneSchema";
import HelperText from "components/Form/HelperText/HelperText";
import { addBusinessDays } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { AllFieldsRequired } from "components/NewOrders/constants";
import { truckActions } from "features/reducers/truck/truck";

interface TruckShippingInfoProps {
	addresses: models["ShipToViewModel"][];
	isAddressesLoading: boolean;
	isAccountsLoading: boolean;
}

const TruckShippingInfo = ({ isAccountsLoading, isAddressesLoading, addresses }: TruckShippingInfoProps) => {
	const {
		control,
		setValue,
		clearErrors,
		formState: { errors },
		watch
	} = useFormContext();
	const { fields, append, remove } = useFieldArray({
		control,
		name: "stops"
	});

	const watchedStops = watch("stops");

	const dispatch = useDispatch();
	const truck = useSelector((state: RootState) => state.truck.truck);
	const truckRequestedDeliveryDate = truck?.requestedDeliveryDate;

	const today = new Date();
	const firstValidDate = addBusinessDays(today, START_BUSINESS_DAY_RANGE);
	const secondValidDate = addBusinessDays(today, END_BUSINESS_DAY_RANGE);

	const [allAddresses, setAllAddresses] = useState<models["ShipToViewModel"][]>([]);
	const [addedAddresses, setAddedAddresses] = useState<models["ShipToViewModel"][]>([]);
	const [isNewAddressModalOpen, setIsNewAddressModalOpen] = useState(false);

	const handleAddStop = () => {
		append({ address: null });
	};

	const handleRemoveStop = (index: number) => {
		remove(index);
	};

	const replaceAddressStop = (index: number, address: models["ShipToViewModel"] | null) => {
		setValue(
			`stops.${index}`,
			{
				address: address?.address,
				shipToId: address?.shipToId ?? ""
			},
			{
				shouldValidate: true,
				shouldDirty: true
			}
		);
	};

	useEffect(() => {
		if (!isAccountsLoading) {
			setValue(
				"stops",
				truck?.stops?.map((stop) => ({
					address: stop.address,
					shipToId: stop.shipToId
				})) ?? fields
			);
		}
		setValue("requestDate", truckRequestedDeliveryDate ? new Date(truckRequestedDeliveryDate) : null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAccountsLoading]); // only run on mount to set the form's initial values, after accounts load

	// handle when account number is changed
	useEffect(() => {
		setAllAddresses([...addresses, ...addedAddresses]);
	}, [addresses, addedAddresses]);

	return (
		<TruckInfoWrapper>
			<span>{ShippingInfoText}</span>
			<span>{AllFieldsRequired}</span>
			<Controller
				control={control}
				name="requestDate"
				render={({
					field: {
						onChange,
						value = truckRequestedDeliveryDate ? new Date(truckRequestedDeliveryDate) : null
					},
					fieldState: { error }
				}) => (
					<RequestDateWrapper isError={!!error}>
						<DatePickerDateField
							label={RequestDateText + " *"}
							value={value}
							firstValidDate={firstValidDate}
							secondValidDate={secondValidDate}
							onChange={(value) => {
								onChange(value);
								dispatch(truckActions.updateRequestDate(value));
								if (
									isDateWithinBusinessDayRange(
										new Date(value),
										START_BUSINESS_DAY_RANGE,
										END_BUSINESS_DAY_RANGE
									)
								) {
									clearErrors("requestDate");
								}
							}}
							isEndIcon
							valueCanBeNull
						/>
						{error && (
							<FormHelperText error>
								<HelperText
									isError={true}
									text={error?.message}
								/>
							</FormHelperText>
						)}
					</RequestDateWrapper>
				)}
			/>
			<TruckShippingInfoContent>
				<div>
					{fields.map((stop, index) => (
						<TruckStopWrapper
							key={`${index} ${stop.id}`}
							hasError={!!errors.stops}
						>
							<span>
								{StopNumberText}
								{index + 1}
							</span>
							<div>
								<Controller
									name={`stops.${index}`}
									control={control}
									render={({ field: { onChange, value = null }, fieldState: { error } }) => (
										<AutoComplete
											options={allAddresses}
											onChange={(_, selectedValue) => {
												onChange(selectedValue);
												replaceAddressStop(index, selectedValue);
											}}
											getOptionLabel={(option: models["TruckStopViewModel"]) =>
												option.address
													? `${option.address.name}, ${option.address.line1}, ${option.address.city}, ${option.address.state} ${option.address.zip}`
													: ""
											}
											isError={!errors.accountNumber && !!error}
											errorText={SelectShippingAddressErrorText}
											label={SelectShippingAddressText}
											dataTestId="truck-info-shipping-address"
											value={value}
											disabled={!watch("accountNumber")}
											isLoading={isAccountsLoading || isAddressesLoading}
											required
										/>
									)}
								/>
								{index > 0 && (
									<Button
										variant="text"
										onClick={() => handleRemoveStop(index)}
									>
										{RemoveText}
									</Button>
								)}
							</div>
							{index === fields.length - 1 && (
								<Button
									onClick={() => setIsNewAddressModalOpen(true)}
									variant="text"
									data-id="truck-add-address-button"
									data-testid="truck-add-address-button"
								>
									{AddNewAddressLink}
								</Button>
							)}
							<AddressFormProvider>
								<AddNewAddressModal
									handleClose={() => setIsNewAddressModalOpen(false)}
									open={isNewAddressModalOpen}
									onSubmit={(address) => {
										const shippingAddress: models["ShipToViewModel"] = {
											shipToId: null,
											isActive: true,
											isSelected: true,
											address: {
												name: address.fullName,
												email: address.email,
												phoneNumber: address.contactPhoneNumber,
												line1: address.address,
												line2: address.address2,
												line3: null,
												city: address.city,
												state: address.state,
												zip: address.zip,
												county: address.county
											}
										};
										setAddedAddresses((prev) => [...prev, shippingAddress]);
										replaceAddressStop(index, shippingAddress);
										setIsNewAddressModalOpen(false);
									}}
								/>
							</AddressFormProvider>
						</TruckStopWrapper>
					))}
				</div>
				{!watchedStops.find((stop: models["TruckStopViewModel"]) => !stop.address) && (
					<Button
						onClick={handleAddStop}
						variant="text"
						startIcon={
							<AddIcon
								fontSize="small"
								sx={{ marginBottom: "2px" }}
							/>
						}
						data-id="truck-add-stop-button"
						data-testid="truck-add-stop-button"
					>
						{AddStopText}
					</Button>
				)}
			</TruckShippingInfoContent>
		</TruckInfoWrapper>
	);
};

export default TruckShippingInfo;
